import type { Map } from 'maplibre-gl';
import { getLevelProperty } from '@wemap/map';
import type { WemapMap } from '@wemap/map';

import InstructionManager from './nav-instruction-manager.js';
import { Itinerary } from '@wemap/routers';
import { EMPTY_FEATURE_COLLECTION, setGeoJsonSourceData } from './geoJsonSource.js';

const SOURCE_ID = 'steps';

export default class StepsHandler {

    private readonly wemapMap: WemapMap;

    private readonly map: Map;

    _itinerary: Itinerary | null = null;

    _onStepClick: ((stepIndex: number) => void) | null = null;

    _selectedStepIndex: number | null = null;

    layersID = ['step', 'step-2'];

    constructor(wemapMap: WemapMap) {
        this.wemapMap = wemapMap;
        this.map = wemapMap.maplibre;

        wemapMap.addSource(SOURCE_ID, {
            type: 'geojson',
            data: EMPTY_FEATURE_COLLECTION,
            promoteId: 'stepIndex',
        });

        wemapMap.addLayer({
            id: 'step',
            type: 'circle',
            source: SOURCE_ID,
            paint: {
                'circle-color': '#0000ff',
                'circle-radius': ['case', ['==', ['get', 'important'], true], 16, 10],
                'circle-opacity': [
                    'case',
                    ['==', ['feature-state', 'selected'], true],
                    1.0,
                    0.6
                ],
                'circle-translate-anchor': 'map',
                'circle-pitch-alignment': 'map'
            }
        }, { indoor: true });

        wemapMap.addLayer({
            id: 'step-2',
            type: 'circle',
            source: SOURCE_ID,
            paint: {
                'circle-color': [
                    'case',
                    ['==', ['feature-state', 'selected'], true],
                    '#00ff00',
                    '#ff0000'
                ],
                'circle-radius': ['case', ['==', ['get', 'important'], true], 10, 6],
                'circle-opacity': [
                    'case',
                    ['==', ['feature-state', 'selected'], true],
                    1.0,
                    0.6
                ],
                'circle-translate-anchor': 'map',
                'circle-pitch-alignment': 'map'
            }
        }, { indoor: true });

        const handleStepClick = (e: any) => {
            e.preventDefault();

            const geojsonProperties = (e.features.length && e.features[0])?.properties;
            const stepIndex = geojsonProperties?.stepIndex;

            if (stepIndex !== undefined && this._onStepClick) {
                this._onStepClick(stepIndex);
            }
        };

        this.map.on('click', 'step', handleStepClick);
        this.map.on('click', 'step-2', handleStepClick);
    }

    set onStepClick(callback: ((stepIndex: number) => void) | null) {
        this._onStepClick = callback;
    }

    set selectedStepIndex(stepIndex: number | null) {
        const previousIndex = this._selectedStepIndex;
        this._selectedStepIndex = stepIndex;
        this._updateFeatureState(previousIndex, stepIndex);
    }

    _updateFeatureState(previousIndex: number | null, newIndex: number | null) {
        if (!this.map.style || !this.map.getSource(SOURCE_ID)) {
            return;
        }

        if (previousIndex !== null) {
            this.map.setFeatureState(
                { source: SOURCE_ID, id: previousIndex },
                { selected: false }
            );
        }

        if (newIndex !== null) {
            this.map.setFeatureState(
                { source: SOURCE_ID, id: newIndex },
                { selected: true }
            );
        }
    }

    set itinerary(itinerary: any) {
        this._itinerary = itinerary;
        this._updateFeatures();
    }

    _updateFeatures() {
        if (!this.map.style) {
            return;
        }

        const steps = this._itinerary?.steps || [];

        const features = steps.map((step: any, index: number) => {
            const instructionInfo = InstructionManager.getInfoFromStep(step);
            const instruction = InstructionManager.getInstructionFromStep(step);

            return {
                type: 'Feature' as const,
                id: index,
                geometry: {
                    type: 'Point' as const,
                    coordinates: [step.coords.lng, step.coords.lat]
                },
                properties: {
                    stepIndex: index,
                    important: Boolean(step.important),
                    instructionInfo,
                    instruction,
                    ...getLevelProperty(step.coords.level)
                }
            };
        });

        setGeoJsonSourceData(this.map, SOURCE_ID, {
            type: 'FeatureCollection',
            features
        });

        if (this._selectedStepIndex !== null) {
            this._updateFeatureState(null, this._selectedStepIndex);
        }
    }

    hideSteps = () => {
        setGeoJsonSourceData(this.map, SOURCE_ID, EMPTY_FEATURE_COLLECTION);
    }

    showSteps = () => {
        this._updateFeatures();
    }
}
